'use client'

import { useMemo } from 'react'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from '@/i18n/routing'
import type {
  StoryChapterNavigation,
  StoryLearningIndexItem,
  StoryMetadata
} from '@/content/stories'
import { useAuth } from '@/hooks/useAuth'
import {
  useLearningState,
  useLearningStates,
  useSetLearningState
} from '@/hooks/useLearningState'
import {
  getStoryProgressDisplay,
  isStoryRead,
  progressPercent
} from '@/lib/content-progress-core'
import {
  learningContentKey,
  storyLearningLocator
} from '@/lib/learning-state-core'
import { cn } from '@/lib/utils'
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconArrowRight,
  IconCircleCheck
} from '@tabler/icons-react'
import { StoryRecommendationsSection } from './story-recommendations-section'

function progressBadge(
  state:
    | {
        status?: string | null
        progress?: number | null
      }
    | null
    | undefined,
  isNext: boolean
) {
  const display = getStoryProgressDisplay(state)
  if (display) {
    return <Badge variant={display.badgeVariant}>{display.label}</Badge>
  }
  if (isNext) return <Badge variant="secondary">Next chapter</Badge>
  return null
}

function ChapterLink({
  chapter,
  state,
  isNext,
  compact = false
}: {
  chapter: StoryMetadata & { isCurrent?: boolean }
  state?: { status?: string | null; progress?: number | null } | null
  isNext: boolean
  compact?: boolean
}) {
  const label = chapter.chapterNumber
    ? `Chapter ${chapter.chapterNumber}`
    : 'Story'

  return (
    <Link
      href={`/read/${chapter.slug}`}
      className={cn(
        'hover:bg-muted/45 focus-visible:ring-ring/40 flex min-w-0 items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm transition focus-visible:ring-2 focus-visible:outline-none',
        compact && 'rounded-md px-2.5 py-2',
        chapter.isCurrent && 'bg-muted/35 border-foreground/20'
      )}
    >
      <span className="min-w-0">
        <span className="text-muted-foreground block text-xs">{label}</span>
        <span className="block truncate font-medium">{chapter.title_en}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {progressBadge(state, isNext)}
        <IconArrowRight className="text-muted-foreground h-4 w-4" />
      </span>
    </Link>
  )
}

function StoryCompletionControl({
  currentSlug,
  currentTitle,
  variant = 'below'
}: {
  currentSlug: string
  currentTitle?: string
  variant?: 'side' | 'below'
}) {
  const { user } = useAuth()
  const locator = useMemo(
    () => storyLearningLocator(currentSlug),
    [currentSlug]
  )
  const { data: state, isLoading } = useLearningState(locator)
  const setLearningState = useSetLearningState()
  const read = isStoryRead(state)
  const display = getStoryProgressDisplay(state)
  const percent = progressPercent(state?.progress)

  const markRead = () => {
    setLearningState.mutate(
      {
        ...locator,
        status: read ? 'studying' : 'completed',
        ...(read ? {} : { progress: 1 }),
        metadata: {
          title: currentTitle,
          href: `/read/${currentSlug}`,
          source: 'story_completion_control'
        }
      },
      {
        onSuccess: () => {
          toast.success(read ? 'Story marked as started' : 'Story marked read')
        },
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Could not update story progress'
          )
        }
      }
    )
  }

  return (
    <section
      className={cn(
        'border-border/70',
        variant === 'side' ? 'border-b pb-5' : 'border-y py-4'
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-3',
          variant === 'below' &&
            'sm:flex-row sm:items-center sm:justify-between'
        )}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold">Reading progress</p>
          <p className="text-muted-foreground mt-1 text-sm">
            {display
              ? percent === null
                ? display.label
                : `${display.label} · ${percent}%`
              : 'Mark this story when you are done.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {display && (
            <Badge variant={display.badgeVariant}>{display.label}</Badge>
          )}
          {!user?.id ? (
            <LoginDialog>
              <Button variant="outline" size="sm">
                Save progress
              </Button>
            </LoginDialog>
          ) : (
            <Button
              variant={read ? 'outline' : 'default'}
              size="sm"
              disabled={isLoading || setLearningState.isPending}
              onClick={markRead}
            >
              {read ? (
                <IconArrowBackUp className="h-4 w-4" />
              ) : (
                <IconCircleCheck className="h-4 w-4" />
              )}
              {read ? 'Mark not read' : 'Mark read'}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

export function StoryContinuationPanel({
  navigation,
  storyLearningIndex,
  currentSlug,
  currentTitle,
  variant = 'below',
  className
}: {
  navigation: StoryChapterNavigation | null
  storyLearningIndex: StoryLearningIndexItem[]
  currentSlug: string
  currentTitle?: string
  variant?: 'side' | 'below'
  className?: string
}) {
  const chapterLocators =
    navigation?.chapters.map((chapter) => storyLearningLocator(chapter.slug)) ??
    []
  const { data: chapterStates = new Map() } = useLearningStates(chapterLocators)
  const nextSlug = navigation?.next?.slug ?? null
  const compact = variant === 'side'

  return (
    <div
      className={cn(
        variant === 'side'
          ? 'space-y-5 text-sm'
          : 'mx-auto w-full max-w-5xl space-y-5 px-4 py-8 md:px-6',
        className
      )}
    >
      <StoryCompletionControl
        currentSlug={currentSlug}
        currentTitle={currentTitle}
        variant={variant}
      />

      {navigation && (
        <section
          className={cn(
            'border-border/70',
            variant === 'side' ? 'border-b pb-5' : 'border-b pb-5'
          )}
        >
          <div
            className={cn(
              'flex flex-col gap-3',
              variant === 'below' &&
                'sm:flex-row sm:items-start sm:justify-between'
            )}
          >
            <div className="min-w-0">
              <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                Series
              </p>
              <h2
                className={cn(
                  'mt-1 font-semibold',
                  variant === 'side' ? 'text-base' : 'text-lg'
                )}
              >
                {navigation.series.title}
              </h2>
              {navigation.series.description && variant === 'below' && (
                <p className="text-muted-foreground mt-1 text-sm">
                  {navigation.series.description}
                </p>
              )}
            </div>
            <div
              className={cn(
                'flex shrink-0 gap-2',
                variant === 'side' && 'grid grid-cols-2'
              )}
            >
              {navigation.previous && (
                <Button asChild variant="outline" size="sm" className="min-w-0">
                  <Link href={`/read/${navigation.previous.slug}`}>
                    <IconArrowLeft className="h-4 w-4" />
                    <span className="truncate">Previous</span>
                  </Link>
                </Button>
              )}
              {navigation.next && (
                <Button asChild size="sm" className="min-w-0">
                  <Link href={`/read/${navigation.next.slug}`}>
                    <span className="truncate">Next</span>
                    <IconArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
          <div className={cn('mt-4 grid gap-2', compact && 'gap-1.5')}>
            {navigation.chapters.map((chapter) => (
              <ChapterLink
                key={chapter.slug}
                chapter={chapter}
                isNext={chapter.slug === nextSlug}
                compact={compact}
                state={chapterStates.get(
                  learningContentKey(storyLearningLocator(chapter.slug))
                )}
              />
            ))}
          </div>
        </section>
      )}

      {!navigation?.next && (
        <section
          className={cn(variant === 'side' && 'border-border/70 border-b pb-5')}
        >
          <StoryRecommendationsSection
            stories={storyLearningIndex}
            currentSlug={currentSlug}
            title="More to read"
            description={
              variant === 'side'
                ? undefined
                : 'A few useful next reads based on your progress and familiarity.'
            }
            limit={3}
          />
        </section>
      )}
    </div>
  )
}
